import { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "magazzinopro.appearance.v1";

export const DEFAULT_APPEARANCE = {
  appName: "MagazzinoPro",
  subtitle: "Gestionale",
  primaryColor: "#0f172a",
  sidebarStyle: "light",
  density: "comfortable",
  radius: "medium",
  showSubtitle: true,
};

const AppearanceContext = createContext(null);

function hexToHsl(hex) {
  const value = hex.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return "222 47% 11%";
  const r = parseInt(value.slice(0, 2), 16) / 255;
  const g = parseInt(value.slice(2, 4), 16) / 255;
  const b = parseInt(value.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    if (max === g) h = (b - r) / d + 2;
    if (max === b) h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function loadAppearance() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return { ...DEFAULT_APPEARANCE, ...(saved || {}) };
  } catch {
    return DEFAULT_APPEARANCE;
  }
}

export function AppearanceProvider({ children }) {
  const [appearance, setAppearance] = useState(loadAppearance);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(appearance));
    const root = document.documentElement;
    root.style.setProperty("--primary", hexToHsl(appearance.primaryColor));
    root.style.setProperty("--ring", hexToHsl(appearance.primaryColor));
    root.dataset.density = appearance.density;
    root.dataset.radius = appearance.radius;
  }, [appearance]);

  const value = useMemo(() => ({
    appearance,
    setAppearance,
    resetAppearance: () => setAppearance(DEFAULT_APPEARANCE),
  }), [appearance]);

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export function useAppearance() {
  const value = useContext(AppearanceContext);
  if (!value) throw new Error("useAppearance deve essere usato dentro AppearanceProvider");
  return value;
}
